import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Space, Tag, message, Switch, Popconfirm } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import request from '../utils/request';

type MenuNode = {
  id: number;
  key: string;
  name: string;
  icon?: string | null;
  type: string;
  sort: number;
  visible: boolean;
  parentId?: number | null;
  children?: MenuNode[];
};

// 与后端 menu.constants.ts 白名单对齐：MENU 叶子的 key 只能选已注册前端路由
const KNOWN_MENU_KEYS = [
  '/', '/departments', '/doctors', '/today-regs', '/registrations',
  '/products', '/categories', '/orders',
  '/points-mall', '/points-accounts', '/points-redemptions',
  '/distributors', '/referrals', '/commissions', '/withdraws',
  '/feedbacks', '/users', '/stores', '/banners', '/contents',
  '/accounts', '/roles', '/menus',
];

// 可选图标名（与 AdminLayout 的 ICON_REGISTRY 一致）
const ICON_OPTIONS = [
  'DashboardOutlined', 'MedicineBoxOutlined', 'UserOutlined', 'FormOutlined', 'SoundOutlined',
  'ShoppingOutlined', 'AppstoreOutlined', 'ProfileOutlined', 'GiftOutlined', 'ShareAltOutlined',
  'MessageOutlined', 'IdcardOutlined', 'ShopOutlined', 'PictureOutlined', 'FileTextOutlined',
  'TeamOutlined', 'SafetyCertificateOutlined', 'MenuUnfoldOutlined',
].map((v) => ({ label: v, value: v }));

// 收集所有分组节点作为「上级」下拉候选
const collectGroups = (nodes: MenuNode[]): { value: number; label: string }[] =>
  nodes.flatMap((n) => (n.type === 'GROUP' ? [{ value: n.id, label: n.name }, ...collectGroups(n.children || [])] : []));

const Menus: React.FC = () => {
  const [tree, setTree] = useState<MenuNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const [presetParent, setPresetParent] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const typeWatch = Form.useWatch('type', form);

  const fetchTree = async () => {
    setLoading(true);
    try {
      const res: any = await request.get('/admin/menu/tree');
      setTree(res || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTree();
  }, []);

  const groupOptions = useMemo(() => collectGroups(tree), [tree]);

  const openCreate = (parentId: number | null) => {
    setEditItem(null);
    setPresetParent(parentId);
    form.resetFields();
    form.setFieldsValue({ type: parentId === null ? 'GROUP' : 'MENU', parentId: parentId ?? undefined, sort: 0, visible: true });
    setModalOpen(true);
  };

  const openEdit = (record: MenuNode) => {
    setEditItem(record);
    setPresetParent(record.parentId ?? null);
    form.setFieldsValue({
      type: record.type,
      name: record.name,
      key: record.key,
      icon: record.icon || undefined,
      parentId: record.parentId ?? undefined,
      sort: record.sort,
      visible: record.visible,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    if (saving) return;
    setSaving(true);
    const payload = {
      type: values.type,
      name: values.name,
      key: values.key,
      icon: values.icon || null,
      parentId: values.parentId ?? null,
      sort: values.sort ?? 0,
      visible: values.visible ?? true,
    };
    try {
      if (editItem) {
        await request.put(`/admin/menu/${editItem.id}`, payload);
      } else {
        await request.post('/admin/menu', payload);
      }
      message.success('保存成功');
      setModalOpen(false);
      fetchTree();
    } finally {
      setSaving(false);
    }
  };

  const toggleVisible = async (record: MenuNode) => {
    if (busyId !== null) return;
    setBusyId(record.id);
    try {
      await request.put(`/admin/menu/${record.id}`, { visible: !record.visible });
      message.success(record.visible ? '已停用' : '已启用');
      fetchTree();
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (record: MenuNode) => {
    await request.delete(`/admin/menu/${record.id}`);
    message.success('已删除');
    fetchTree();
  };

  const columns = [
    { title: '菜单名称', dataIndex: 'name' },
    {
      title: '类型',
      dataIndex: 'type',
      width: 90,
      render: (v: string) => (v === 'GROUP' ? <Tag color="purple">分组</Tag> : <Tag color="blue">菜单</Tag>),
    },
    { title: '标识 key', dataIndex: 'key', width: 180 },
    { title: '图标', dataIndex: 'icon', width: 160, render: (v: string) => v || '-' },
    { title: '排序', dataIndex: 'sort', width: 70 },
    {
      title: '显示',
      dataIndex: 'visible',
      width: 80,
      render: (v: boolean, r: MenuNode) => (
        <Switch size="small" checked={v} loading={busyId === r.id} onChange={() => toggleVisible(r)} />
      ),
    },
    {
      title: '操作',
      width: 260,
      render: (_: any, record: MenuNode) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>编辑</Button>
          {record.type === 'GROUP' && (
            <Button size="small" onClick={() => openCreate(record.id)}>加子项</Button>
          )}
          <Popconfirm title="确认删除该菜单？" onConfirm={() => handleDelete(record)} okText="删除" cancelText="取消">
            <Button size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>菜单管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate(null)}>
          新建顶级菜单
        </Button>
      </div>

      <Table columns={columns} dataSource={tree} rowKey="id" loading={loading}
        pagination={false} expandable={{ defaultExpandAllRows: true }}
        childrenColumnName="children" />

      <Modal
        title={editItem ? '编辑菜单' : presetParent === null ? '新建顶级菜单' : '新建子菜单'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => !saving && setModalOpen(false)}
        okButtonProps={{ loading: saving }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="type" label="类型" rules={[{ required: true }]}>
            <Select options={[{ label: '分组（容器）', value: 'GROUP' }, { label: '菜单（页面）', value: 'MENU' }]} />
          </Form.Item>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如：商城" />
          </Form.Item>
          {typeWatch === 'MENU' ? (
            <Form.Item name="key" label="页面路由 key" rules={[{ required: true, message: '请选择路由' }]}
              extra="菜单叶子只能绑定已注册的前端路由">
              <Select options={KNOWN_MENU_KEYS.map((k) => ({ label: k, value: k }))} showSearch />
            </Form.Item>
          ) : (
            <Form.Item name="key" label="分组标识 key" rules={[{ required: true, message: '请输入分组标识' }]}
              extra="分组用合成标识，建议以 g: 开头，如 g:shop">
              <Input placeholder="如：g:shop" />
            </Form.Item>
          )}
          <Form.Item name="icon" label="图标" extra="仅分组与带图标菜单需要">
            <Select allowClear options={ICON_OPTIONS} placeholder="选择图标" />
          </Form.Item>
          <Form.Item name="parentId" label="上级分组">
            <Select allowClear options={groupOptions.filter((o) => o.value !== editItem?.id)} placeholder="不填为顶级" />
          </Form.Item>
          <Form.Item name="sort" label="排序" extra="数值越小越靠前">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="visible" label="是否显示" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Menus;
